import {
  CurrencyField,
  Card,
  Row,
  Col,
  Margin,
  PrimaryCTALink,
} from '@ovotech/nebula';
import { useAccountContext } from '@ovotech/ui-tools';
import React, { FormEvent } from 'react';
import { Redirect } from 'react-router-dom-v5';
import { ReducedPaddingCard } from './SettingsPanel.styled';
import { DIRECT_DEBIT_CALCULATOR as DIRECT_DEBIT_CALCULATOR_ANALYTICS } from '@/src/constants/analytics';
import {
  ROUTE_ADJUST_DD_AMOUNT_CALCULATION,
  ROUTE_HOME,
} from '@/src/constants/routes';
import {
  ContractsResponse,
  ProjectedCostsResponse,
} from '@/src/types/Response';
import { formatFullDate, getEpochDaysDate } from '@/src/utils/date';
import { isRelatedMPANCustomer } from '@/src/utils/supplyPoints';
import { useSupplyPointsQuery } from '@/src/api/kapi/use-supply-points-query';
import { RouterLink } from '@/src/components';
import useWindowDimensions from '@/src/utils/hooks/useWindowDimensions';
import { areContractsVariable } from '@/src/utils/contracts';
import { useDirectDebitQuery } from '@/src/api';

type SettingsPanelProps = {
  newDirectDebitAmount: string;
  contracts: ContractsResponse;
  handleAmountChange: (e: FormEvent<HTMLInputElement>) => void;
  projectedCosts: ProjectedCostsResponse;
  isStandardDDCustomer: boolean;
};

const SettingsPanel = ({
  newDirectDebitAmount,
  isStandardDDCustomer,
  contracts,
  projectedCosts,
  handleAmountChange,
}: SettingsPanelProps) => {
  const { accountId } = useAccountContext();
  const supplyPointsQueryResult = useSupplyPointsQuery();
  const directDebit = useDirectDebitQuery(accountId);
  const { isDesktop } = useWindowDimensions({ debounceDelay: 0 });

  if (!accountId) return null;

  if (supplyPointsQueryResult.isLoading) return null;

  if (supplyPointsQueryResult.isError || !supplyPointsQueryResult.data) {
    return null;
  }

  const activeOrFutureContracts = contracts.electricity
    .concat(contracts.gas)
    .filter(
      c =>
        c.expiryDate == null ||
        getEpochDaysDate(c.expiryDate).getTime() > Date.now(),
    );

  if (
    !activeOrFutureContracts.length ||
    isRelatedMPANCustomer(
      supplyPointsQueryResult.data?.account?.accountSupplyPoints ?? [],
    )
  ) {
    return <Redirect to={ROUTE_HOME} />;
  }

  const hasDirectDebit = directDebit.data?.amount !== null;
  const ownsDirectDebit = directDebit.data?.customerOwnsMandate !== false;

  const analyticsProps = JSON.stringify({
    currentDirectDebit: directDebit?.data?.amount?.amount,
    ownsDd: ownsDirectDebit,
    hasDirectDebit: hasDirectDebit,
    newDirectDebit: newDirectDebitAmount,
  });

  const isVariable = areContractsVariable(contracts);

  const dataTestId = isVariable
    ? 'dd-calc-settings-variable'
    : 'dd-calc-settings';

  return (
    <Margin bottom={1}>
      <Card as={ReducedPaddingCard} data-testid={dataTestId}>
        <Row isNested>
          <Col large={12} medium={12} small={12}>
            <CurrencyField
              id="amount"
              label="Enter amount:"
              value={newDirectDebitAmount}
              onChange={handleAmountChange}
              min={1}
              max={2000}
              characters={10}
            />
          </Col>
        </Row>
      </Card>
      {isDesktop ? (
        <>
          <Margin top={4}>
            {projectedCosts &&
              (isStandardDDCustomer
                ? `The amount predicted by the calculator might change based on your actual energy use costs. To help cover any changes over the course of the year, your Direct Debit payments are calculated to make sure you have credit for 1 month’s payment in your OVO account by ${formatFullDate(
                    projectedCosts.recommendedDirectDebit.calculationEndDate,
                  )}.`
                : 'The amount predicted by the calculator might change based on your actual energy use costs.')}
          </Margin>
          <Margin top={4}>
            <PrimaryCTALink
              to={ROUTE_ADJUST_DD_AMOUNT_CALCULATION}
              as={RouterLink}
              data-event-name={
                DIRECT_DEBIT_CALCULATOR_ANALYTICS.CLICKED_CHANGE_DIRECT_DEBIT_AMOUNT
              }
              data-event-props={analyticsProps}
            >
              <span
                data-event-name={
                  DIRECT_DEBIT_CALCULATOR_ANALYTICS.CLICKED_CHANGE_DIRECT_DEBIT_AMOUNT
                }
                data-event-props={analyticsProps}
              >
                Continue
              </span>
            </PrimaryCTALink>
          </Margin>
        </>
      ) : null}
    </Margin>
  );
};

export default SettingsPanel;
